import {Elastic, Sine, TimelineMax} from "gsap";
import { PlayMode } from "html-living-framework/lib/Audio/VoiceOverManager";
import {Application} from "../Application";
import {BaseButton, ButtonEventListener, HoverableButton, TextureButton} from "../core/Button";
import {IPoint} from "../core/Geom";
import {Mix} from "../core/Reflection";
import { OutlineFilter } from "../filters/OutlineFilter";
import {AudioChannel} from "../managers/AudioChannel";
import {Focusable} from "./Focusable";

export const PulsatingButton = {
    // Do not use this argument here because it's not compiled properly with arguments having default values
    pulse(scale: number = 1.15, interval: number = 1.5, startImmediately: boolean = true) {
        const b = this as any as BaseButton;
        (this as any).stopPulse();
        const initScale = b.scale.x;
        const loop = new TimelineMax({repeat: -1})
            .to(this, 0.15, {
                scaleX: initScale * scale,
                scaleY: initScale * scale,
                ease: Sine.easeInOut,
            }, interval)
            .to(this, 1.0, {
                scaleX: initScale,
                scaleY: initScale,
                ease: Elastic.easeOut,
            })
            .call(() => {
                loop.seek(0);
                if (b.isOver) {
                    loop.pause();
                }
            });
        if (startImmediately) {
            loop.seek(interval);
        }
        const onOut = () => {
            loop.resume();
        };
        b.onOut.add(onOut);
        const destroy = b.destroy;
        b.destroy = () => {
            loop.kill();
            destroy.call(b);
        };
        (this as any)._pulsatingButtonOriginalDestroy = destroy;
        (this as any)._pulsatingButtonLoop = loop;
        (this as any)._pulsatingButtonOnOut = onOut;
        (this as any)._pulsatingButtonInitScale = initScale;
    },
    stopPulse(this: BaseButton) {
        if ((this as any)._pulsatingButtonLoop) {
            this.destroy = (this as any)._pulsatingButtonOriginalDestroy;
            this.onOut.remove((this as any)._pulsatingButtonOnOut);
            (this as any)._pulsatingButtonLoop.kill();
            (this as any)._pulsatingButtonLoop = undefined;
            this.scaleTo((this as any)._pulsatingButtonInitScale, 0.15, Elastic.easeOut);
        }
    },
};

export class Button extends Mix(TextureButton, HoverableButton(), PulsatingButton, Focusable) {

    public clickSfx?: string;
    public hoverVO?: string;
    public clickSfxCanOverlap: boolean = true;

    constructor(texture?: PIXI.Texture | string, onClick?: ButtonEventListener<Button>, anchor?: IPoint) {
        super(texture, onClick, anchor);

        this.onOver.add(() => {
            if (this.hoverVO) {
                Application.instance.voiceover.playVO(this.hoverVO, {mode: PlayMode.Override, priority: -1});
            }
        });
        this.onDown.add(() => {
            if (this.clickSfx) {
                if (this.clickSfxCanOverlap) {
                    AudioChannel.SFX.play(this.clickSfx);
                }
                else if (!AudioChannel.SFX.get(this.clickSfx)?.isPlaying()) {
                    AudioChannel.SFX.play(this.clickSfx);
                }
            }
        });
    }
}

export class OutlinedButton extends Button {

    public outlineFilter = new OutlineFilter(5, 0xffff00);

    constructor(texture?: PIXI.Texture | string, onClick?: ButtonEventListener<Button>, anchor?: IPoint) {
        super(texture, onClick, anchor);
        this.visuals.filters = [this.outlineFilter];
        this.outlineFilter.enabled = false;

        this.onOver.add(() => {
            this.outlineFilter.enabled = true;
        });
        this.onOut.add(() => {
            this.outlineFilter.enabled = false;
        });
        this.onDown.add(() => {
            this.outlineFilter.enabled = true;
        });
        this.onClick.add(() => {
            this.outlineFilter.enabled = false;
        });
    }
}
